import { Link } from "react-router-dom";
import { Linkedin, Youtube } from "lucide-react";
import { AIPROF_PHONE } from "./ContactCTA";

// New X (formerly Twitter) brand logo — lucide-react doesn't ship it.
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const SOCIALS = [
  { Icon: XIcon, href: "https://x.com/AIProfHQ", label: "x" },
  { Icon: Linkedin, href: "https://www.linkedin.com/company/aiprofhq/", label: "linkedin" },
  { Icon: Youtube, href: "https://youtube.com/@aiprofhq", label: "youtube" },
];

// title + links. Each link = [label, to]. `to` may be a #hash (in-page scroll)
// or a /route (opens in a new tab).
const COLUMNS = [
  {
    title: "Program",
    links: [
      ["AI & ML", "#program"],
      ["Data Science", "#program"],
      ["Cyber Security", "#program"],
      ["CSE", "#program"],
      ["IT", "#program"],
    ],
  },
  {
    title: "Explore",
    links: [
      ["Curriculum", "#curriculum"],
      ["Mentors", "#mentors"],
      ["Founder", "#founder"],
      ["FAQ", "#faq"],
      ["Admissions", "#admissions"],
    ],
  },
  {
    title: "Apply",
    links: [
      ["Talk to our Expert", "/RFC/guidance"],
      ["Download Brochure", "/RFC/brochure"],
    ],
  },
];

function FooterLink({ label, to }) {
  const cls = "text-sm text-white/60 hover:text-aiorange transition";
  if (to.startsWith("#")) {
    return (
      <a href={to} className={cls}>
        {label}
      </a>
    );
  }
  // Contact link is device-aware: dialpad on mobile, contact form on desktop.
  if (to === "/RFC/guidance") {
    return (
      <>
        <a href={`tel:${AIPROF_PHONE}`} className={`${cls} block lg:hidden`}>
          {label}
        </a>
        <Link
          to={to}
          target="_blank"
          rel="noopener noreferrer"
          className={`${cls} hidden lg:block`}
        >
          {label}
        </Link>
      </>
    );
  }
  return (
    <Link to={to} target="_blank" rel="noopener noreferrer" className={cls}>
      {label}
    </Link>
  );
}

export default function SmruFooter() {
  return (
    <footer className="bg-[#0A0A0A] text-white pt-20 pb-10 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-20"
        style={{
          background:
            "radial-gradient(circle at 80% 20%, #0052FF, transparent 60%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
        <div className="grid lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr] gap-10 pb-14">
          {/* Brand */}
          <div>
            <div className="inline-flex items-center gap-3 rounded-2xl bg-white px-4 py-2.5 shadow-lg">
              <img src="/ai_prof.png" alt="AI.Prof" className="h-8 w-auto object-contain" />
              <span className="text-aiorange font-black text-xl leading-none">×</span>
              <img
                src="/partners/smru/smrulogo.png"
                alt="St. Mary's University (SMRU)"
                className="h-10 w-auto object-contain"
              />
            </div>
            <p className="text-white/70 max-w-sm leading-relaxed text-sm mt-5">
              India's First AI-Powered B.Tech — St. Mary's University,
              Hyderabad. Built for students going global.
            </p>
            <div className="flex gap-3 mt-6">
              {SOCIALS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="h-10 w-10 rounded-full bg-white/5 hover:bg-aiblue flex items-center justify-center transition"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h4 className="font-display font-bold text-sm uppercase tracking-widest mb-4 text-white/90">
                {col.title}
              </h4>
              <ul className="space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={label}>
                    <FooterLink label={label} to={to} />
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Get in touch */}
          <div>
            <h4 className="font-display font-bold text-sm uppercase tracking-widest mb-4 text-white/90">
              Get in Touch
            </h4>
            <a
              href="mailto:support@aiprof.com"
              className="text-sm text-white/60 hover:text-aiorange block"
            >
              support@aiprof.com
            </a>
            <a
              href="tel:+91 8071 580 580"
              className="text-sm text-white/60 hover:text-aiorange block mt-1"
            >
              +91 8071 580 580
            </a>
            <p className="text-sm text-white/60 mt-2">
              Made in Hyderabad, India 🇮🇳
            </p>
          </div>
        </div>

        {/* bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
          <span>© 2026 AI.Prof. All rights reserved.</span>
          <div className="flex gap-4">
            <a href="https://aiprof.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-aiorange transition">
              Privacy
            </a>
            <a href="https://aiprof.com/legal/terms" target="_blank" rel="noopener noreferrer" className="hover:text-aiorange transition">
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
